/**
 * Inside a master set or a sub set: every slot in scope, owned bright and
 * missing faded.
 *
 * Chase's instruction: "tapping in is EXACTLY like the new explore and we see
 * all and what we own is colors or brighter, and dont own is a bit faded." So
 * this is the Explore surface scoped to one set, and it is the first grid in
 * the app that draws a card nobody owns - every other grid draws rows that
 * exist because somebody has them.
 *
 * WHY THIS IS NOT CatalogCardGrid.
 *
 * That grid draws catalogue CARDS and this one draws SLOTS, which are (card,
 * printing) pairs: four Base Set Charizards, one image, four badges. It would
 * need a faded state, a top badge and a synthetic id per tile, and the file is
 * shared with Explore and the picker. What it does own and this file reuses is
 * the zoom: `zoomGrid` and `ZOOM_COLUMNS` are imported rather than restated, so
 * the three sizes mean the same thing here as everywhere else.
 *
 * WHERE THE NUMBERS COME FROM.
 *
 * The bar is `wish_set_progress` and the tiles are `wish_set_slot_grid`, and
 * since phase 5 those are two reads of the SAME rows: the ownership rule lives
 * once, in `wish_set_slot_rows`, and the count is that grid's owned rows. There
 * is nothing left here to reconcile, which is why the phase 4 tripwire is gone.
 *
 * On somebody else's list there is no ownership to draw at all: the grid counts
 * against whoever is asking, so fading their Charizard would be fading OUR
 * Charizard under their set name. Their grid is every slot at full brightness,
 * with the size of the set and no bar.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRegisterGridControls } from '../../context/GridControlsContext';
import useZoom from '../../hooks/useZoom';
import { Info, MoreHorizontal } from 'lucide-react';
import BottomSheet from '../BottomSheet';
import { zoomGrid } from '../collection/CatalogCardGrid';
import SortFilterModal from '../modals/SortFilterModal';
import WishSurfaceHoldSheet from './WishSurfaceHoldSheet';
import * as cardData from '../../services/cardData';
import { fetchSlotGrid } from '../../services/wishSlots';
import { buildSlotTiles, printingsOf } from './setSlots';
import { ARTWORK_DISCLOSURE, ARTWORK_DISCLOSURE_SHORT } from './printings';
import {
  readSetGridPrefs, writeSetGridPrefs, DEFAULT_SET_GRID_SORT,
} from './setSlotPrefs';
import { EMPTY_CATALOG_FILTERS, hasCatalogFilters, countCatalogFilters,
} from '../../services/cardData/catalogFilters';
import { formatMoney } from '../../utils/catalogPrices';
import { isTcgPocketSet } from '../../utils/tcgPocket';
import { holdProps } from './wishHold';
import haptics from '../../utils/haptics';
import {
  SlotGridHeader, SlotTile, slotGridStyles,
} from './slotGridParts';

/**
 * The most catalogue cards one set read asks for.
 *
 * The largest set in the catalogue holds a few hundred, so this is headroom
 * rather than a limit anybody meets - and it is a cap rather than an unbounded
 * read so a set that grows past all expectation still returns something.
 */
const CARD_CAP = 799;

/**
 * How many tiles are put in the DOM at once, and how many arrive per step.
 *
 * Base Set is 408 tiles and the phone is the target, so the grid grows as it is
 * scrolled instead of mounting every tile at first paint. The art itself is
 * already lazy - CardImage observes the app's own scroller - but four hundred
 * mounted tiles is four hundred subtrees and four hundred observers, which is
 * what makes a long grid feel heavy even with no image loaded.
 */
const FIRST_PAGE = 60;
const PAGE_STEP = 60;

export default function SetSlotGrid({
  row,
  progress,
  asUserId = null,
  theme,
  isDark = true,
  pricingEnabled = false,
  readOnly = false,
  onBack,
  onShare,
  onRemove,
}) {
  const setId = row?.set_id;
  const scope = row?.scope || null;

  const [slots, setSlots] = useState(null);
  const [cards, setCards] = useState(null);
  const [failed, setFailed] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [heldSurface, setHeldSurface] = useState(false);
  // Collapsed by default. Three sentences above a grid is a third of a card row
  // spent on a point most people take in once and never need again, so the
  // short form sits there and the full wording is one tap away.
  const [disclosureOpen, setDisclosureOpen] = useState(false);
  const [openSlot, setOpenSlot] = useState(null);
  const [shown, setShown] = useState(FIRST_PAGE);

  // Read once on the way in rather than on every render of a grid that can hold
  // four hundred tiles.
  // Zoom is the app's one preference, not this screen's. See
  // utils/zoomPreference.js: sort and filter are remembered per place because
  // they are about the task, zoom is not because it is about your eyes.
  const { zoom, cycleZoom } = useZoom();
  const [sort, setSort] = useState(() => readSetGridPrefs().sort);
  const [filters, setFilters] = useState(() => readSetGridPrefs().filters);
  const [missingOnly, setMissingOnly] = useState(() => readSetGridPrefs().missingOnly);

  useEffect(() => {
    writeSetGridPrefs({ sort, filters, missingOnly });
  }, [zoom, sort, filters, missingOnly]);

  const [eras, setEras] = useState([]);
  useEffect(() => {
    let alive = true;
    cardData.getEras()
      .then((rows) => { if (alive) setEras(rows || []); })
      .catch((e) => console.warn('[slot grid] era load failed', e?.message));
    return () => { alive = false; };
  }, []);

  // One read for the whole grid, and it does not depend on sort or filters, so
  // it happens once for the set rather than again on every re-order. The scope
  // is applied server-side, which is why it is a dependency here.
  useEffect(() => {
    if (!setId) return undefined;
    let alive = true;
    setSlots(null);
    fetchSlotGrid(setId, scope, asUserId)
      .then((rows) => { if (alive) setSlots(rows); })
      .catch((e) => {
        console.warn('[slot grid] could not read the slots', e?.message);
        if (alive) { setSlots([]); setFailed(true); }
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setId, asUserId, JSON.stringify(scope)]);

  // The order and the filtering are the database's, the same as Explore and the
  // picker, so the grid never sorts rows it can only see a page of.
  useEffect(() => {
    if (!setId) return undefined;
    let alive = true;
    setCards(null);
    cardData.listCatalogCards({ setId, from: 0, to: CARD_CAP, sort, filters })
      .then(({ rows }) => { if (alive) setCards(rows); })
      .catch((e) => {
        console.warn('[slot grid] could not read the set', e?.message);
        if (alive) { setCards([]); setFailed(true); }
      });
    return () => { alive = false; };
  }, [setId, sort, filters]);

  // Always badge the printing.
  //
  // This used to be suppressed inside a sub set, on the reasoning that a 1st
  // Edition scope says the same word on every tile of a screen whose header
  // already says it. That was wrong, and the owner caught it: "throughout the
  // app we need that little marker of the variant. Image has 1st ed base set,
  // all should say 1st ed." A card is not a card plus a header - it gets
  // screenshotted, shared, scrolled past and compared against another screen,
  // and the printing is the difference between a $40 card and a $400 one. The
  // marker belongs ON the card, not only above it.
  const printings = useMemo(() => printingsOf(slots || []), [slots]);
  const badges = true;

  const tiles = useMemo(() => {
    if (!slots || !cards) return [];
    return buildSlotTiles({
      slots,
      cards,
      ownership: !readOnly,
      missingOnly: readOnly ? false : missingOnly,
      badges,
    });
  }, [slots, cards, readOnly, missingOnly, badges]);

  useEffect(() => { setShown(FIRST_PAGE); }, [sort, filters, missingOnly, setId]);

  /* Zoom, sort and missing-only go on the page's toolbar beside the search,
     the way Cards and Binder have always had them, rather than on a second row
     under this title. */
  useRegisterGridControls({
    onZoom: cycleZoom,
    onSort: () => setSortOpen(true),
    sortActive: hasCatalogFilters(filters),
    filterCount: countCatalogFilters(filters),
    missingOnly: readOnly ? null : missingOnly,
    onMissingOnly: readOnly ? null : () => setMissingOnly((v) => !v),
  }, [cycleZoom, filters, missingOnly, readOnly]);

  const sentinel = useRef(null);
  const grow = useCallback(() => setShown((n) => n + PAGE_STEP), []);
  useEffect(() => {
    const node = sentinel.current;
    if (!node || shown >= tiles.length) return undefined;
    if (typeof IntersectionObserver === 'undefined') { grow(); return undefined; }
    // The app scrolls inside .app-content rather than the document, the same
    // scroller CardImage has to be told about.
    const root = node.closest('.app-content') || null;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) grow();
    }, { root, rootMargin: '400px 0px', threshold: 0.01 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [shown, tiles.length, grow]);

  const held = useRef(false);
  const loading = slots === null || cards === null;
  const slotsInScope = Number(progress?.slots) || 0;
  const pocket = isTcgPocketSet(setId);

  return (
    <div style={styles.wrap}>
      {/* Name and fraction stacked into one block, then the controls - the
          Binder tab's two rows, where the bar used to take a panel of its own
          between them.

          Share and stop chasing are still on a hold, which is where Chase put
          them, and the hold moved onto the identity block with the fraction.
          The line that used to say so is gone and the (...) button says it
          instead: a sentence of instructions cost a row of the screen to
          describe an action that a visible button simply offers. The hold is
          the same fixed long-press the rows use, cancelled by the scroll event
          itself, so a thumb resting on the title while the grid slides past
          does not open a menu. */}
      <SlotGridHeader
        title={row?.name}
        onBack={onBack}
        backLabel="Back to the chase list"
        theme={theme}
        progress={{
          total: slotsInScope,
          filled: progress ? progress.filled : null,
          noun: 'slots',
          pending: !progress,
        }}
        testId="slot-grid-progress"
        hint={readOnly
          ? "Whose cards are whose is not shown on somebody else's list."
          : null}
        holdHandlers={holdProps({
          onHold: () => { haptics.heavy(); setHeldSurface(true); },
          firedRef: held,
          disabled: readOnly,
        })}
        onContextMenu={(e) => { e.preventDefault(); if (!readOnly) setHeldSurface(true); }}
        trailing={!readOnly && (
          <button
            style={{ ...styles.iconBtn, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.text }}
            onClick={() => { haptics.tap(); setHeldSurface(true); }}
            aria-label="Share this set or stop chasing it"
          >
            <MoreHorizontal size={18} />
          </button>
        )}
        toolbar={null}
      />

      {/* Said once, above the grid that repeats art, and not on the tiles. Four
          hundred copies of a disclosure is a nag; one line where somebody is
          about to see four identical Charizards is the truth arriving when it
          is needed. */}
      {badges && (
        <button
          style={{ ...styles.disclosure, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.textMuted }}
          onClick={() => { haptics.tap(); setDisclosureOpen((v) => !v); }}
          aria-expanded={disclosureOpen}
        >
          <Info size={14} color={theme.textMuted} style={styles.disclosureIcon} />
          <span style={disclosureOpen ? styles.disclosureFull : styles.disclosureLine}>
            {disclosureOpen ? ARTWORK_DISCLOSURE : ARTWORK_DISCLOSURE_SHORT}
          </span>
        </button>
      )}

      {loading && (
        <p style={{ ...styles.state, color: theme.textSecondary }}>Laying out the slots...</p>
      )}
      {!loading && failed && (
        <p style={{ ...styles.state, color: theme.textSecondary }}>
          The slots could not be read just now. The count above is still good.
        </p>
      )}
      {!loading && !failed && tiles.length === 0 && (
        <p style={{ ...styles.state, color: theme.textSecondary }}>
          {missingOnly
            ? 'Nothing missing here. Every slot in this scope is filled.'
            : 'No slots in here match all of that. Try unticking one.'}
        </p>
      )}

      {tiles.length > 0 && (
        <>
          <div style={{ ...styles.grid, ...zoomGrid(zoom) }}>
            {tiles.slice(0, shown).map((tile) => (
              <SlotTile
                key={tile.id}
                tile={tile}
                captions={zoom > 0}
                theme={theme}
                isDark={isDark}
                pocket={pocket}
                pricingEnabled={pricingEnabled}
                neutral={readOnly}
                onOpen={() => { haptics.tap(); setOpenSlot(tile); }}
              />
            ))}
          </div>
          <div ref={sentinel} style={styles.sentinel}>
            {shown < tiles.length && (
              <span style={{ ...styles.state, color: theme.textSecondary }}>
                {tiles.length - shown} more slots...
              </span>
            )}
          </div>
        </>
      )}

      <SortFilterModal
        isOpen={sortOpen}
        onClose={() => setSortOpen(false)}
        /* Catalogue mode, which is the set of orders a fixed list of cards can
           honestly be put in - and it carries no Best Match, which would mean
           nothing inside one set with no query over it. */
        mode="catalog"
        isOwnCollection
        sortBy={sort}
        setSortBy={setSort}
        catalogFilters={filters}
        setCatalogFilters={setFilters}
        catalogEras={eras}
        onReset={() => { setFilters(EMPTY_CATALOG_FILTERS); setSort(DEFAULT_SET_GRID_SORT); }}
      />

      <WishSurfaceHoldSheet
        isOpen={heldSurface}
        surface={row}
        kindLabel={scope ? 'sub set' : 'master set'}
        onClose={() => setHeldSurface(false)}
        onShare={onShare}
        onRemove={onRemove}
      />

      <SlotSheet
        tile={openSlot}
        theme={theme}
        pocket={pocket}
        pricingEnabled={pricingEnabled}
        neutral={readOnly}
        onClose={() => setOpenSlot(null)}
      />
    </div>
  );
}

/**
 * What a tapped slot says.
 *
 * A slot is not a card, so it does not open the card detail screen: three of
 * the four tiles a person can tap here are printings they do not own and there
 * is no row behind them. What it can answer honestly is which printing this
 * slot is, what it costs, and whether they have it - including the one case
 * that needs saying out loud, where the bar counted a card whose printing
 * nobody ever recorded.
 */
function SlotSheet({ tile, theme, pocket, pricingEnabled, neutral, onClose }) {
  return (
    <BottomSheet
      open={!!tile}
      onClose={onClose}
      label="Slot"
      overlayStyle={{ ...styles.overlay, backgroundColor: theme.overlay }}
      sheetStyle={{ ...styles.sheet, backgroundColor: theme.bgSheet }}
    >
      <div style={{ ...styles.handle, backgroundColor: theme.border }} />
      {tile && (
        <div style={styles.sheetBody}>
          <span style={{ ...styles.sheetTitle, color: theme.text }}>{tile.name}</span>
          <span style={{ ...styles.sheetMeta, color: theme.textMuted }}>
            {tile.setName || tile.setId} · {tile.localId}
          </span>
          {tile.badge && (
            <div style={{ ...styles.sheetBadge, backgroundColor: tile.badge.color }}>
              <span style={{ ...styles.badgeText, color: tile.badge.textColor }}>{tile.badge.full}</span>
            </div>
          )}
          {pricingEnabled && !pocket && formatMoney(tile.price) && (
            <span style={{ ...styles.sheetLine, color: theme.text }}>
              {formatMoney(tile.price)} near mint
            </span>
          )}
          {!neutral && (
            <span style={{ ...styles.sheetLine, color: tile.owned ? theme.success : theme.textMuted }}>
              {tile.owned && !tile.printingUnknown && 'You have this slot filled.'}
              {tile.owned && tile.printingUnknown
                && 'You have this card, but no printing was recorded on it - so it fills one slot of the master set and no printing sub set.'}
              {!tile.owned && 'Still chasing this one.'}
            </span>
          )}
          {tile.badge && (
            <span style={{ ...styles.sheetNote, color: theme.textSecondary }}>{ARTWORK_DISCLOSURE}</span>
          )}
        </div>
      )}
    </BottomSheet>
  );
}

const styles = {
  // The grid, its sentinel and its loading lines are the shared ones, so this
  // surface and a chase binder cannot drift into two different five-across
  // grids with two different gaps.
  ...slotGridStyles,
  wrap: { display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px 12px 32px' },
  iconBtn: {
    width: '44px', height: '44px', borderRadius: '12px', border: '1px solid', display: 'flex',
    alignItems: 'center', justifyContent: 'center', cursor: 'pointer', outline: 'none', flexShrink: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  disclosure: {
    display: 'flex', alignItems: 'flex-start', gap: '8px', width: '100%',
    margin: 0, padding: '9px 11px', borderRadius: '10px', border: '1px solid',
    fontSize: '12px', lineHeight: 1.45, textAlign: 'left',
    cursor: 'pointer', outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
  disclosureLine: {
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
  },
  disclosureFull: { minWidth: 0 },
  disclosureIcon: { flexShrink: 0, marginTop: '2px' },
  badgeText: {
    fontSize: '9px', fontWeight: '700', letterSpacing: '0.2px',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block',
  },
  overlay: { zIndex: 1200 },
  sheet: { borderRadius: '18px 18px 0 0', padding: '10px 16px 24px' },
  handle: { width: '36px', height: '4px', borderRadius: '2px', margin: '0 auto 12px' },
  sheetBody: { display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' },
  sheetTitle: { fontSize: '17px', fontWeight: '700' },
  sheetMeta: { fontSize: '12px' },
  sheetBadge: { padding: '4px 8px', borderRadius: '6px' },
  sheetLine: { fontSize: '13px', lineHeight: 1.45 },
  sheetNote: { fontSize: '11.5px', lineHeight: 1.45 },
};
